import { gql } from '@apollo/client';
import { Spinner } from 'components/spinner';
import {
  Tags,
  useSearchTagsLazyQuery,
  SearchTagsQueryResult,
  useAddTagMutation,
} from 'lib/graphql-generated';
import { Dispatch, SetStateAction } from 'react';
import { MultiSelect, MultiSelectData } from './multi-select';

const SEARCH_TAGS = gql`
  query SearchTags($search: String) {
    tags(where: { text: { _ilike: $search } }) {
      id
      text
    }
  }
`;

const ADD_TAG = gql`
  mutation AddTag($text: String) {
    insert_tags_one(object: { text: $text }) {
      text
      id
    }
  }
`;

export type TagSelectorValue = MultiSelectData<
  SearchTagsQueryResult['data']['tags'][0]
>;

export const SerializeTags = (tags: Partial<Tags>[]): TagSelectorValue[] => {
  const arr =
    tags?.map(
      (tag) =>
        tag && {
          label: tag.text,
          key: tag.id,
          data: { id: tag.id, text: tag.text },
        }
    ) ?? [];

  return arr.filter((obj) => obj);
};

interface Props {
  label?: string;
  value: TagSelectorValue[];
  setValue: Dispatch<SetStateAction<MultiSelectData<Partial<Tags>>[]>>;
  singleSelect?: boolean;
  className?: string;
}

export function TagsSelector({
  label,
  value,
  setValue,
  singleSelect = false,
  className,
}: Props) {
  const [search, { data, loading, error }] = useSearchTagsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [
    addTag,
    { data: newTagData, error: newTagError },
  ] = useAddTagMutation();

  const handleAddTag = async (text: string): Promise<TagSelectorValue> => {
    return addTag({
      variables: {
        text: text,
      },
    }).then(({ data }) => {
      return SerializeTags([data.insert_tags_one])[0];
    });
  };

  const handleSearch = (value) => {
    if (value.length >= 2) {
      search({ variables: { search: `%${value}%` } });
    }
  };

  return (
    <MultiSelect
      id={label?.replace(' ', '') || 'tagSelector'}
      label={label}
      onSearch={handleSearch}
      placeholder='Search by tag name'
      searchResults={data?.tags.map((tag) => ({
        key: tag.id,
        label: tag.text,
        data: tag,
      }))}
      values={value}
      setValues={setValue}
      notFoundContent={
        loading ? <Spinner /> : 'No tags were found matching that name'
      }
      singleSelect={singleSelect}
      addNew={handleAddTag}
      className={className}
    />
  );
}
